'use client';

import {useState} from 'react';
import {Icon} from '@udemy-v2/icons';
import {BlockListItem, BlockListProps, Button, IconButton} from '@udemy-v2/react-core-components';
import {Duration, useI18n} from '@udemy-v2/i18n';
import classNames from 'classnames';
import {Lecture as LectureType} from '@udemy-v2/browse-course';
import {curriculumItemIcon} from './curriculumItemIcon';
import {CoursePreviewModal} from '../course-preview/course-preview-modal.react-component';

import styles from './curriculum-section.module.scss';

// Extended interface to add description and AI summary properties that may be available in some contexts
export interface CurriculumLecture extends LectureType {
    description?: string;
    aiSummary?: string;
    contentSummary?: string;
}

export interface LectureComponentProps {
    lecture: CurriculumLecture;
    showSectionContentsText?: boolean;
}

export const Lecture = ({lecture, showSectionContentsText = false}: LectureComponentProps) => {
    const {gettext} = useI18n();
    const [showDescription, setShowDescription] = useState(false);
    const [showModal, setShowModal] = useState(false);

    // Prefer AI summary over instructor description
    // AI summary is plain text, instructor description is HTML
    const descriptionContent = lecture.aiSummary ? (
        <p>{lecture.aiSummary}</p>
    ) : lecture.description ? (
        <span dangerouslySetInnerHTML={{__html: lecture.description}} />
    ) : null;

    const LectureIcon = (
        <Icon color="neutral" glyph={curriculumItemIcon(lecture.type)} label={false} />
    );

    const blockListItemProps: BlockListProps = {
        size: 'small',
    };

    const toggleDescription = (event: React.MouseEvent) => {
        setShowDescription(!showDescription);
        event.stopPropagation(); // Do not trigger handleClickPreview
    };

    const ToggleDescriptionIcon = showDescription ? (
        <Icon color="neutral" glyph="collapse" label={gettext('Hide lecture description')} />
    ) : (
        <Icon color="neutral" glyph="expand" label={gettext('Show lecture description')} />
    );

    const shouldShowPreviewLink =
        lecture.isPreviewable && showSectionContentsText && lecture.durationInSeconds;

    const handleOpenModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const title = <span className={styles['course-lecture-title']}>{lecture.title}</span>;

    return (
        <>
            <BlockListItem icon={LectureIcon} {...blockListItemProps} color="neutral">
                <div>
                    <div className={styles.row}>
                        {title}
                        {descriptionContent && (
                            <IconButton
                                onClick={toggleDescription}
                                round={true}
                                size="small"
                                className={classNames(
                                    styles['hidden-on-mobile'],
                                    styles['toggle-item-description-button'],
                                )}
                                udStyle="ghost"
                                aria-expanded={showDescription}
                                data-testid="toggle-description-button"
                            >
                                {ToggleDescriptionIcon}
                            </IconButton>
                        )}
                        <span className={styles['hidden-on-mobile']} style={{flex: 1}} />
                        {shouldShowPreviewLink && (
                            <>
                                <Button
                                    udStyle="link-underline"
                                    typography="ud-text-sm"
                                    onClick={handleOpenModal}
                                >
                                    <span className={classNames(styles['preview-text'])}>
                                        <Icon
                                            glyph="play"
                                            label={false}
                                            color="neutral"
                                            className={styles.icon}
                                        />
                                        {gettext('Preview')}
                                    </span>
                                </Button>
                            </>
                        )}
                        {lecture.durationInSeconds ? (
                            <span
                                className={classNames(
                                    styles['hidden-on-mobile'],
                                    styles['item-content-summary'],
                                )}
                            >
                                <Duration
                                    numSeconds={lecture.durationInSeconds}
                                    precision="seconds"
                                    presentationStyle="timestamp"
                                />
                            </span>
                        ) : null}
                    </div>
                    {descriptionContent && (
                        <div
                            className={classNames(
                                styles['hidden-on-mobile'],
                                styles['item-description'],
                                'ud-text-sm',
                                {[styles.collapsed]: !showDescription},
                            )}
                            data-testid="lecture-description"
                        >
                            {descriptionContent}
                        </div>
                    )}
                </div>
            </BlockListItem>
            <CoursePreviewModal
                isOpen={showModal}
                onClose={handleCloseModal}
                uiRegion="curriculum"
                selectedLectureId={lecture.id}
            />
        </>
    );
};
